import { v4 as uuid } from 'uuid';
import { ID, LineItem } from 'types';
import { addDefaultsToCCItem } from './addDefaultsToCCItem';

/**
 * Adds default values to a line item
 *
 * @param headerIndexToId - Mapping of header index to ID
 * @param lineItem - The line item to process
 * @param liSno - The sequence number for the line item
 * @param lotId - The ID of the lot
 * @returns {LineItem} - The line item with all required defaults
 */
export const addDefaultsToLineItem = (
  headerIndexToId: Record<number, string>,
  lineItem: Partial<LineItem>,
  liSno: number,
  lotId: ID
): LineItem => {
  const defaults = {
    extended_quantity: 1,
    name: '',
    price: 0,
    quantity: 1,
    units: '',
  };
  const lineItemId = lineItem.id ?? `new-${uuid()}`;
  const customColumnItems = lineItem.custom_column_items
    ? lineItem.custom_column_items.map(cci =>
      addDefaultsToCCItem(cci, headerIndexToId, lineItemId))
    : [];

  return {
    custom_column_items: customColumnItems,
    extended_quantity: lineItem.extended_quantity ?? defaults.extended_quantity,
    id: lineItemId,
    ...(lineItem.is_required !== undefined && { is_required: lineItem.is_required }),
    lot_id: lotId,
    name: lineItem.name ?? defaults.name,
    ...(lineItem.negotiation_starting_price !== undefined && {
      negotiation_starting_price: lineItem.negotiation_starting_price,
    }),
    price: lineItem.price ?? defaults.price,
    quantity: lineItem.quantity ?? defaults.quantity,
    sno: lineItem.sno ?? liSno,
    // Preserve the buyer's imported Suggested Price override from createLineItem. This function
    // whitelists fields, so without the passthrough the import rebuild would drop the override
    // and it would never persist. Conditional (not `?? null`) so an absent key stays absent:
    // a blank cell must leave suggested_price_override untouched, which is how the row signals
    // "let AI pricing suggest it".
    ...(lineItem.suggested_price_override !== undefined && {
      suggested_price_override: lineItem.suggested_price_override,
    }),
    units: lineItem.units ?? defaults.units,
    // Preserve the write-once is_buyer_provided_baseline_price marker stamped by createLineItem (NSB #3/#7). Without
    // this passthrough the import rebuild would drop it, so it would never persist and NSB would reclaim it.
    ...(lineItem.is_buyer_provided_baseline_price !== undefined && { is_buyer_provided_baseline_price: lineItem.is_buyer_provided_baseline_price }),
  };
};
